import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import './components.css';
import Branding from './Branding';
import posthog from '../utils/posthog-stub'; // FERPA: Using no-op stub until self-hosted PostHog is configured

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [tokenValid, setTokenValid] = useState(null);
  const [checkingToken, setCheckingToken] = useState(true);
  
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Check if the token is valid when component mounts
  useEffect(() => {
    if (!token) {
      setError('No reset token provided');
      setTokenValid(false);
      setCheckingToken(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await axios.get(`/api/auth/verify-reset-token?token=${token}`);
        setTokenValid(response.data.valid);
        if (!response.data.valid) {
          setError(response.data.message || 'Invalid or expired reset token');
        }
      } catch (error) {
        console.error('Token verification error:', error);
        setError('Invalid or expired reset token');
        setTokenValid(false);
      } finally {
        setCheckingToken(false);
      }
    };

    verifyToken();
  }, [token]);

  const validatePassword = (password) => {
    if (password.length < 6) {
      return 'Password must be at least 6 characters long';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    // Validate password
    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    // Check password confirmation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/auth/reset-password', {
        token,
        new_password: password
      });

      // Track password reset completion in PostHog
      try {
        posthog.capture('password_reset_completed');
      } catch (err) {
        console.error('PostHog tracking error:', err);
      }

      setMessage(response.data.message);
      setLoading(false);

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.response?.data?.error || 'An error occurred');
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    // Track return to login in PostHog
    try {
      posthog.capture('returned_to_login_from_reset_password');
    } catch (err) {
      console.error('PostHog tracking error:', err);
    }
    
    navigate('/login');
  };

  // Show loading state while checking token
  if (checkingToken) {
    return (
      <div className="login-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: Branding.gradients.headerGradient,
        padding: Branding.spacing.md
      }}>
        <div className="login-card" style={{
          backgroundColor: Branding.colors.pureWhite,
          borderRadius: Branding.borders.radius.lg,
          boxShadow: Branding.shadows.lg,
          padding: Branding.spacing.xl,
          width: '100%',
          maxWidth: '440px',
          textAlign: 'center'
        }}>
          <h2 style={{
            color: Branding.colors.deepPurple,
            marginBottom: Branding.spacing.lg,
            fontWeight: Branding.typography.fontWeight.bold,
            fontSize: Branding.typography.fontSize['2xl']
          }}>Verifying Reset Token...</h2>
          <div style={{
            color: Branding.colors.slateGray,
            fontSize: Branding.typography.fontSize.base
          }}>
            Please wait while we verify your password reset link.
          </div>
        </div>
      </div>
    );
  }

  // Show error state if token is invalid
  if (tokenValid === false) {
    return (
      <div className="login-container" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: Branding.gradients.headerGradient,
        padding: Branding.spacing.md
      }}>
        <div className="login-card" style={{
          backgroundColor: Branding.colors.pureWhite,
          borderRadius: Branding.borders.radius.lg,
          boxShadow: Branding.shadows.lg,
          padding: Branding.spacing.xl,
          width: '100%',
          maxWidth: '440px'
        }}>
          <h2 style={{
            color: Branding.colors.deepPurple,
            marginBottom: Branding.spacing.xs,
            textAlign: 'center',
            fontWeight: Branding.typography.fontWeight.bold,
            fontSize: Branding.typography.fontSize['2xl']
          }}>Invalid Reset Link</h2>
          
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #ef4444',
            borderRadius: Branding.borders.radius.md,
            color: '#b91c1c',
            padding: Branding.spacing.md,
            marginBottom: Branding.spacing.lg,
            fontSize: Branding.typography.fontSize.sm,
            textAlign: 'center'
          }}>
            {error}
          </div>
          
          <p style={{
            color: Branding.colors.slateGray,
            textAlign: 'center',
            marginBottom: Branding.spacing.lg,
            fontSize: Branding.typography.fontSize.base
          }}>
            The password reset link is invalid or has expired. Please request a new one.
          </p>
          
          <button 
            type="button"
            onClick={() => navigate('/forgot-password')}
            style={{
              width: '100%',
              padding: Branding.spacing.md,
              background: Branding.gradients.timerGradient,
              color: Branding.colors.pureWhite,
              border: 'none',
              borderRadius: Branding.borders.radius.md,
              fontSize: Branding.typography.fontSize.base,
              fontWeight: Branding.typography.fontWeight.semibold,
              cursor: 'pointer',
              transition: Branding.transitions.default,
              boxShadow: Branding.shadows.md,
              marginBottom: Branding.spacing.md
            }}
          >
            Request New Reset Link
          </button>
          
          <div style={{
            textAlign: 'center'
          }}>
            <button 
              type="button"
              onClick={handleBackToLogin}
              style={{
                background: 'none',
                border: 'none',
                color: Branding.colors.tealBlue,
                cursor: 'pointer',
                fontWeight: Branding.typography.fontWeight.medium,
                fontSize: Branding.typography.fontSize.sm,
                textDecoration: 'underline'
              }}
            >
              ← Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show password reset form if token is valid
  return (
    <div className="login-container" style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      background: Branding.gradients.headerGradient,
      padding: Branding.spacing.md
    }}>
      <div className="login-card" style={{
        backgroundColor: Branding.colors.pureWhite,
        borderRadius: Branding.borders.radius.lg,
        boxShadow: Branding.shadows.lg,
        padding: Branding.spacing.xl,
        width: '100%',
        maxWidth: '440px'
      }}>
        <h2 style={{
          color: Branding.colors.deepPurple,
          marginBottom: Branding.spacing.xs,
          textAlign: 'center',
          fontWeight: Branding.typography.fontWeight.bold,
          fontSize: Branding.typography.fontSize['2xl']
        }}>Set New Password</h2>
        <p style={{
          color: Branding.colors.slateGray,
          textAlign: 'center',
          marginBottom: Branding.spacing.lg,
          fontSize: Branding.typography.fontSize.base
        }}>
          Enter your new password below. Make sure it's at least 6 characters long.
        </p>
        
        {error && <div style={{
          backgroundColor: '#fee2e2',
          border: '1px solid #ef4444',
          borderRadius: Branding.borders.radius.md,
          color: '#b91c1c',
          padding: Branding.spacing.md,
          marginBottom: Branding.spacing.md,
          fontSize: Branding.typography.fontSize.sm
        }}>{error}</div>}
        
        {message && <div style={{
          backgroundColor: '#dcfce7',
          border: '1px solid #22c55e',
          borderRadius: Branding.borders.radius.md,
          color: '#15803d',
          padding: Branding.spacing.md,
          marginBottom: Branding.spacing.md,
          fontSize: Branding.typography.fontSize.sm,
          textAlign: 'center'
        }}>
          <div>{message}</div>
          <div style={{ marginTop: Branding.spacing.xs, fontSize: Branding.typography.fontSize.xs }}>
            Redirecting to login in a few seconds...
          </div>
        </div>}
        
        {!message && (
          <form onSubmit={handleSubmit}>
            <div style={{
              marginBottom: Branding.spacing.md,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <label htmlFor="password" style={{
                display: 'block',
                marginBottom: Branding.spacing.xs,
                fontWeight: Branding.typography.fontWeight.medium,
                color: Branding.colors.darkSlate,
                fontSize: Branding.typography.fontSize.sm
              }}>New Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: Branding.spacing.md,
                  border: `1px solid ${Branding.colors.veryLightGray}`,
                  borderRadius: Branding.borders.radius.md,
                  fontSize: Branding.typography.fontSize.base,
                  transition: Branding.transitions.default
                }}
                placeholder="Enter your new password"
              />
            </div>
            
            <div style={{
              marginBottom: Branding.spacing.lg,
              display: 'flex',
              flexDirection: 'column'
            }}>
              <label htmlFor="confirmPassword" style={{
                display: 'block',
                marginBottom: Branding.spacing.xs,
                fontWeight: Branding.typography.fontWeight.medium,
                color: Branding.colors.darkSlate,
                fontSize: Branding.typography.fontSize.sm
              }}>Confirm New Password</label>
              <input
                type="password"
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: Branding.spacing.md,
                  border: `1px solid ${Branding.colors.veryLightGray}`,
                  borderRadius: Branding.borders.radius.md,
                  fontSize: Branding.typography.fontSize.base,
                  transition: Branding.transitions.default
                }}
                placeholder="Confirm your new password"
              />
            </div>
            
            <button 
              type="submit" 
              disabled={loading}
              style={{
                width: '100%',
                padding: Branding.spacing.md,
                background: Branding.gradients.timerGradient,
                color: Branding.colors.pureWhite,
                border: 'none',
                borderRadius: Branding.borders.radius.md,
                fontSize: Branding.typography.fontSize.base,
                fontWeight: Branding.typography.fontWeight.semibold,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: Branding.transitions.default,
                boxShadow: Branding.shadows.md,
                opacity: loading ? 0.8 : 1
              }}
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </button>
          </form>
        )}
        
        {!message && (
          <div style={{
            marginTop: Branding.spacing.xl,
            textAlign: 'center'
          }}>
            <button 
              type="button"
              onClick={handleBackToLogin}
              style={{
                background: 'none',
                border: 'none',
                color: Branding.colors.tealBlue,
                cursor: 'pointer',
                fontWeight: Branding.typography.fontWeight.medium,
                fontSize: Branding.typography.fontSize.sm,
                textDecoration: 'underline'
              }}
            >
              ← Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;